import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import type { Flow } from 'lib-shared-types-api';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { HookFormText } from 'components/hookForm';
import { notificationActions } from 'components/notifications/actions/notification';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './flowModal.module.scss';
import { modalTitle } from './util/helper';
import { useUpdateFlow } from './util/useUpdateFlow';

const formId = 'flow-form';

type FieldValues = Pick<Flow, 'name'>;

/** A modal for updating flows. */
export const FlowModal = createAppModal(
  { type: 'settings:flow' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const { flow, mode = 'rename' } = modal.arg;

    const form = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        name: flow.name,
      },
    });

    const updateFlow = useUpdateFlow();
    const dispatch = useAppDispatch();

    const [isLoading, setIsLoading] = useState(false);

    const handleValidSubmit: SubmitHandler<FieldValues> = (values) => {
      setIsLoading(true);

      if (mode === 'rename') {
        handleRenameFlow(values);
      }
    };

    const handleRenameFlow = async ({ name }: FieldValues) => {
      try {
        await updateFlow({ flow: { name }, id: flow.id });

        dispatch(
          notificationActions.notificationAdd({
            title: 'Flow updated',
            text: `"${name}" flow has been updated`,
            icon: 'i-done',
            className: 'success',
            time: 5000,
          }),
        );

        meta?.resolve({ isConfirmed: true, isCancelled: false });

        hide?.();
      } catch (error) {
        console.warn(error);

        dispatch(
          notificationActions.notificationAdd({
            title: 'An error occurred',
            text: `An error occurred when trying to update the "${name}" flow`,
            className: 'danger',
            icon: 'i-exclamation',
          }),
        );

        setIsLoading(false);
      }
    };

    return (
      <Modal className={styles.root} isOpen={isOpen} onClose={hide}>
        <ModalHead title={modalTitle[mode]} onClose={hide} />

        <ModalBody>
          <form id={formId} onSubmit={form.handleSubmit(handleValidSubmit)}>
            <FormGrid>
              <FormField>
                <HookFormText
                  name="name"
                  label="Name"
                  control={form.control}
                  rules={{
                    required: 'Name is required',
                  }}
                />
              </FormField>
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName="Update"
          confirmOnClick={form.handleSubmit(handleValidSubmit)}
          confirmProps={{
            type: 'submit',
            form: formId,
            disabled: !form.formState.isDirty || !form.formState.isValid,
            isLoading,
          }}
        />
      </Modal>
    );
  },
);
